/**
 * CalendarLegend — el botó «?» al costat de la roda dentada del calendari, amb la llegenda dels
 * senyals de la card (`plans/calendar-card-signals.md` §6 F2.4).
 *
 * Tots els colors, símbols i textos surten de `signal-styles.ts`, el mateix que pinta la card: si
 * un color canvia allà, la llegenda canvia sola.
 */

import type { CSSProperties, ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  CONTACT_LEGEND,
  CONTINUES_STAY_MARK,
  GROUP_BADGE_STYLES,
  LIGHT_STYLES,
  MOVE_LOCK_ALERT_MARK,
  MOVE_LOCK_MARK,
  PAY_ON_ARRIVAL_MARK,
  PAYMENT_NONE_LABEL,
  PAYMENT_STRIPE_ORDER,
  PAYMENT_STRIPE_STYLES,
  PAYMENT_STRIPE_WIDTH,
  PAYMENT_VIA_ORDER,
  PAYMENT_VIA_STYLES,
  REGISTRATION_LEGEND,
  STAY_LEGEND,
  type LegendEntry,
  type LightKind,
} from './signal-styles';

/** Mida dels llums de la llegenda: la de la card a la fila normal, amb símbol. */
const LEGEND_LIGHT_SIZE = 16;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
      <ul className="flex flex-col gap-1">{children}</ul>
    </section>
  );
}

function Row({ mark, label, state }: { mark: ReactNode; label: string; state: string }) {
  return (
    <li className="flex items-start gap-2 text-xs text-gray-800" data-legend-state={state}>
      <span className="flex h-4 w-6 shrink-0 items-center justify-center">{mark}</span>
      <span className="min-w-0">{label}</span>
    </li>
  );
}

function StripeSwatch({ background }: { background: string | null }) {
  const style: CSSProperties = {
    width: PAYMENT_STRIPE_WIDTH,
    height: LEGEND_LIGHT_SIZE,
    background: background ?? 'transparent',
    border: background ? undefined : '1px dashed #CBD5E1',
  };
  return <span aria-hidden="true" className="block" style={style} />;
}

export function LegendLight({ kind }: { kind: LightKind }) {
  const s = LIGHT_STYLES[kind];
  const style: CSSProperties = {
    width: LEGEND_LIGHT_SIZE,
    height: LEGEND_LIGHT_SIZE,
    background: s.background,
    border: s.borderWidth > 0 ? `${s.borderWidth}px solid ${s.borderColor}` : undefined,
    color: s.color,
    fontSize: 11,
    lineHeight: 1,
  };
  return (
    <span
      aria-hidden="true"
      className="flex items-center justify-center font-bold box-border"
      style={style}
      data-light-kind={kind}
    >
      {s.glyph}
    </span>
  );
}

function LightRows<K extends string>({ entries }: { entries: readonly LegendEntry<K>[] }) {
  return (
    <>
      {entries.map((e) => (
        <Row key={e.state} state={e.state} mark={<LegendLight kind={e.kind} />} label={e.label} />
      ))}
    </>
  );
}

/** El contingut de la llegenda, sense el popover (els tests el pinten directament). */
export function CalendarLegendContent() {
  const master = GROUP_BADGE_STYLES.master;
  const member = GROUP_BADGE_STYLES.member;
  const badge = (s: typeof master | typeof member) => (
    <span
      aria-hidden="true"
      className="rounded px-1 text-[10px] font-semibold leading-4"
      style={{ background: s.background, color: s.color, border: `1px solid ${s.borderColor}` }}
    >
      G3
    </span>
  );

  return (
    <div className="flex flex-col gap-3" data-testid="calendar-legend">
      <p className="text-xs text-gray-600">Si no hi ha color, no hi ha feina.</p>

      <Section title="Franja de pagament (esquerra)">
        <Row state="NONE" mark={<StripeSwatch background={null} />} label={PAYMENT_NONE_LABEL} />
        {PAYMENT_STRIPE_ORDER.map((stripe) => (
          <Row
            key={stripe}
            state={stripe}
            mark={<StripeSwatch background={PAYMENT_STRIPE_STYLES[stripe].background} />}
            label={PAYMENT_STRIPE_STYLES[stripe].label}
          />
        ))}
      </Section>

      <Section title="Llum de dalt: entrada i sortida">
        <LightRows entries={STAY_LEGEND} />
      </Section>

      <Section title="Llum del mig: registre">
        <LightRows entries={REGISTRATION_LEGEND} />
      </Section>

      <Section title="Llum de baix: contacte">
        <LightRows entries={CONTACT_LEGEND} />
      </Section>

      <Section title="Al costat del nom">
        <Row state="GROUP_MASTER" mark={badge(master)} label={master.label} />
        <Row state="GROUP_MEMBER" mark={badge(member)} label={member.label} />
        <Row
          state="PAY_ON_ARRIVAL"
          mark={
            <span className="font-bold" style={{ color: PAY_ON_ARRIVAL_MARK.color }}>
              {PAY_ON_ARRIVAL_MARK.glyph}
            </span>
          }
          label={PAY_ON_ARRIVAL_MARK.label}
        />
        <Row
          state="CONTINUES_STAY"
          mark={
            <span className="font-bold" style={{ color: CONTINUES_STAY_MARK.color }}>
              {CONTINUES_STAY_MARK.glyph}
            </span>
          }
          label={CONTINUES_STAY_MARK.label}
        />
        {/* Bloqueig a l'habitació (`plans/reservation-move-lock.md` §3). */}
        <Row
          state="MOVE_LOCK"
          mark={<span style={{ color: MOVE_LOCK_MARK.color }}>{MOVE_LOCK_MARK.glyph}</span>}
          label={MOVE_LOCK_MARK.label}
        />
        <Row
          state="MOVE_LOCK_ALERT"
          mark={
            <span
              className="rounded px-0.5 text-[9px] font-semibold leading-4 whitespace-nowrap"
              style={{ background: MOVE_LOCK_ALERT_MARK.background, color: MOVE_LOCK_ALERT_MARK.color }}
            >
              {MOVE_LOCK_ALERT_MARK.glyph}
            </span>
          }
          label={`«${MOVE_LOCK_ALERT_MARK.glyph} ${MOVE_LOCK_ALERT_MARK.text}»: ${MOVE_LOCK_ALERT_MARK.label}`}
        />
      </Section>

      <Section title="Com s'ha pagat (línia 3)">
        {PAYMENT_VIA_ORDER.map((via) => (
          <Row
            key={via}
            state={via}
            mark={<span className="text-[11px] font-semibold">{PAYMENT_VIA_STYLES[via].short}</span>}
            label={PAYMENT_VIA_STYLES[via].label}
          />
        ))}
      </Section>
    </div>
  );
}

/** Botó «?» amb la llegenda en un `Popover` de Shadcn. */
export function CalendarLegend() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
          aria-label="Llegenda del calendari"
          title="Llegenda"
        >
          <HelpCircle className="w-5 h-5 text-gray-600" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={8}
        className="w-[min(360px,calc(100vw-32px))] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-3"
      >
        <CalendarLegendContent />
      </PopoverContent>
    </Popover>
  );
}

export default CalendarLegend;
